import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { Sparkles, X } from 'lucide-react';
import type { TourState, TourStatus } from '@fint/validation';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/features/common/ui';
import { TOURS, stepsFor, tourToOffer, type Tour } from './tours';

/** Which tour is running, and where in it. Started from the offer card, the ? menu or Settings. */
interface TourRun {
  tourId: string | null;
  step: number;
  start: (tourId: string) => void;
  go: (step: number) => void;
  stop: () => void;
}

export const useTourRun = create<TourRun>((set) => ({
  tourId: null,
  step: 0,
  start: (tourId) => set({ tourId, step: 0 }),
  go: (step) => set({ step }),
  stop: () => set({ tourId: null, step: 0 }),
}));

export const TOUR_STATE_KEY = ['me', 'tours'] as const;

/** The person's tour marks, and how to record one. */
export function useTourState() {
  const signedIn = useAuthStore((s) => !!s.user);
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: TOUR_STATE_KEY,
    queryFn: () => api.get<TourState>('/me/tours'),
    enabled: signedIn,
    staleTime: 5 * 60_000,
  });
  const mark = useMutation({
    mutationFn: ({ tourId, status }: { tourId: string; status: TourStatus }) => api.put<TourState>(`/me/tours/${tourId}`, { status }),
    onSuccess: (next) => queryClient.setQueryData(TOUR_STATE_KEY, next),
  });
  return { state: state.data, mark: (tourId: string, status: TourStatus) => mark.mutate({ tourId, status }) };
}

/** The tours this person can take, each with the steps their role allows. */
export function useAvailableTours(): Array<{ tour: Tour; steps: number }> {
  const perms = usePermissions();
  const manyBooks = useAuthStore((s) => s.orgs.length > 1);
  return useMemo(
    () =>
      TOURS.map((tour) => ({ tour, steps: stepsFor(tour, (r, a) => perms.can(r, a), manyBooks).length })).filter((t) => t.steps > 0),
    [perms, manyBooks],
  );
}

/** The first VISIBLE element matching the selector: the wide sidebar, the rail and the phone differ. */
function findTarget(selector: string | undefined): HTMLElement | null {
  if (!selector) return null;
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && getComputedStyle(el).visibility !== 'hidden') return el;
  }
  return null;
}

/**
 * The offer, and the tour itself - mounted once, in the layout.
 *
 * The offer is a small card in a corner, never a dialog over the page: a
 * person who opened Fint to raise an invoice is not stopped to be shown round.
 * "Not now" records a skip, which is what starts the six-month clock.
 *
 * The tour dims the screen around the step's element and puts a card beside
 * it; a step on another page goes there first. A step whose element is not on
 * this screen (the sidebar on a phone) shows its card in the middle instead -
 * the words are the step, the highlight is a help.
 */
export function TourHost() {
  const perms = usePermissions();
  const manyBooks = useAuthStore((s) => s.orgs.length > 1);
  const { state, mark } = useTourState();
  const run = useTourRun();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerClosed, setOfferClosed] = useState(false);

  const can = useCallback((r: string, a: 'view' | 'create' | 'edit' | 'manage') => perms.can(r, a), [perms]);
  const offer = useMemo(() => (offerClosed || run.tourId ? null : tourToOffer(state, can, manyBooks, new Date())), [state, can, manyBooks, offerClosed, run.tourId]);

  const tour = run.tourId ? TOURS.find((t) => t.id === run.tourId) ?? null : null;
  const steps = useMemo(() => (tour ? stepsFor(tour, can, manyBooks) : []), [tour, can, manyBooks]);
  const step = steps[run.step];

  // A step on another page goes there first.
  useEffect(() => {
    if (step?.route && location.pathname !== step.route) navigate(step.route);
  }, [step, location.pathname, navigate]);

  // The element, found when it appears (a page that is still loading) and
  // followed as the page scrolls or resizes.
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!step) return;
    let stopped = false;
    let tries = 0;
    const measure = () => {
      if (stopped) return;
      const el = findTarget(step.target);
      if (el) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
        if (step.target && tries++ < 20) setTimeout(measure, 150);
      }
    };
    measure();
    const onMove = () => {
      const el = findTarget(step.target);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      stopped = true;
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [step, location.pathname]);

  const finish = useCallback(
    (status: TourStatus) => {
      if (run.tourId) mark(run.tourId, status);
      run.stop();
    },
    [run, mark],
  );

  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish('skipped');
      if (e.key === 'ArrowRight') run.step < steps.length - 1 ? run.go(run.step + 1) : finish('done');
      if (e.key === 'ArrowLeft' && run.step > 0) run.go(run.step - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, run, steps.length, finish]);

  if (step && tour) {
    const pad = 6;
    const last = run.step === steps.length - 1;
    // The card beside the element: below it if there is room, else above,
    // else to its right (a sidebar entry); in the middle with no element.
    const cardWidth = Math.min(340, window.innerWidth - 24);
    let cardStyle: React.CSSProperties = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: cardWidth };
    if (rect) {
      const below = rect.bottom + pad + 12;
      const roomBelow = window.innerHeight - below > 200;
      const nearLeft = rect.right < window.innerWidth / 3 && rect.width < 300;
      if (nearLeft) cardStyle = { left: rect.right + pad + 12, top: Math.max(12, Math.min(rect.top - 8, window.innerHeight - 240)), width: cardWidth };
      else if (roomBelow) cardStyle = { left: Math.max(12, Math.min(rect.left, window.innerWidth - cardWidth - 12)), top: below, width: cardWidth };
      else cardStyle = { left: Math.max(12, Math.min(rect.left, window.innerWidth - cardWidth - 12)), top: Math.max(12, rect.top - pad - 12 - 200), width: cardWidth };
    }
    return createPortal(
      <div className="fixed inset-0 z-[60] print:hidden" data-testid="tour" role="dialog" aria-modal="true" aria-label={tour.title}>
        {rect ? (
          // The cut-out: a transparent box over the element whose enormous
          // shadow is the dim everywhere else.
          <div
            className="pointer-events-none absolute rounded-lg ring-2 ring-primary transition-all duration-200"
            style={{
              left: rect.left - pad,
              top: rect.top - pad,
              width: rect.width + pad * 2,
              height: rect.height + pad * 2,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-black/45" />
        )}
        <div className="absolute rounded-lg border border-border bg-card p-4 text-card-foreground shadow-xl" style={cardStyle} data-testid="tour-card">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {tour.title} · {run.step + 1} of {steps.length}
            </p>
            <button type="button" onClick={() => finish('skipped')} aria-label="Close the tour" className="-m-1 rounded-md p-1 text-muted-foreground hover:bg-accent">
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <h2 className="mt-1 text-base font-semibold" data-testid="tour-title">
            {step.title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => finish('skipped')} data-testid="tour-skip">
              Skip tour
            </Button>
            <span className="flex-1" />
            {run.step > 0 ? (
              <Button size="sm" variant="outline" onClick={() => run.go(run.step - 1)}>
                Back
              </Button>
            ) : null}
            <Button size="sm" onClick={() => (last ? finish('done') : run.go(run.step + 1))} data-testid="tour-next">
              {last ? 'Done' : 'Next'}
            </Button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (!offer) return null;
  return (
    <div
      className="fixed bottom-[calc(5rem+var(--safe-bottom))] right-4 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg print:hidden sm:bottom-4"
      data-testid="tour-offer"
      role="status"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{offer.title}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{offer.pitch}</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => run.start(offer.id)} data-testid="tour-offer-start">
              Show me
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                mark(offer.id, 'skipped');
                setOfferClosed(true);
              }}
              data-testid="tour-offer-later"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
